import React, { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";

export type LazyMountWhenVisibleProps = {
  children: React.ReactNode;
  /** Reserved height before mount so the page doesn't jump when the block appears. */
  minHeight?: number | string;
  /** Start mounting slightly before the block enters the viewport. */
  rootMargin?: string;
  className?: string;
  /** Optional test / debug attribute. */
  "data-lazy-mount"?: string;
};

/**
 * Mounts children once the sentinel intersects the viewport (then stays mounted).
 * Used to defer heavy Statistics Overview chart fetches until scroll.
 */
export function LazyMountWhenVisible({
  children,
  minHeight = 320,
  rootMargin = "240px 0px",
  className,
  "data-lazy-mount": dataLazyMount,
}: LazyMountWhenVisibleProps): React.ReactElement {
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    if (mounted) return;
    const node = sentinelRef.current;
    if (!node) return;

    if (typeof IntersectionObserver === "undefined") {
      setMounted(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setMounted(true);
          observer.disconnect();
        }
      },
      { root: null, rootMargin, threshold: 0 }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [mounted, rootMargin]);

  return (
    <div
      ref={sentinelRef}
      className={cn("w-full", className)}
      style={mounted ? undefined : { minHeight }}
      data-lazy-mount={dataLazyMount}
      data-lazy-mounted={mounted ? "true" : "false"}
    >
      {mounted ? children : null}
    </div>
  );
}
